/**
 * Session state (GET /api/auth/session) and gate routing. The session query
 * is the SPA's source of truth for who is signed in, which kitchen is active,
 * and which onboarding gate (if any) must be cleared first.
 */
import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

export type Session = components['schemas']['SessionOut'];

export const sessionKey = ['session'] as const;

export const sessionQuery = queryOptions({
  queryKey: sessionKey,
  queryFn: () => unwrap(api.GET('/api/auth/session')),
  staleTime: 30_000,
});

export function useSession() {
  return useQuery(sessionQuery);
}

/** Where a signed-in user must go before anything else, or null. Same
 *  order as the backend gates (backend/pleach/deps.py). */
export function gateRoute(s: Session | undefined): string | null {
  if (!s || !s.authenticated || !s.gates) return null;
  const g = s.gates;
  if (g.password_change_required) return '/change-password';
  if (g.account_closed) return '/account-closed';
  if (g.tos_required) return '/terms';
  if (g.onboarding_required) return '/welcome';
  return null;
}

/** Map an API gate error code to the page that clears it. */
export function routeForGateCode(code: string): string | null {
  switch (code) {
    case 'not_authenticated':
    case 'account_deactivated':
      return '/login';
    case 'password_change_required':
      return '/change-password';
    case 'tos_required':
      return '/terms';
    case 'account_closed':
      return '/account-closed';
    case 'onboarding_required':
      return '/welcome';
    default:
      return null;
  }
}

/** The beta-locked sections: the keys of BETA_FEATURES
 *  (backend/pleach/services/access.py, re-exported by deps.py). Each has a
 *  stage, and what opens depends on the kitchen's level (docs/ACCESS.md;
 *  session.features and lib/access.ts). A locked one shows its status badge
 *  in the nav and the ComingSoon page. */
export type BetaFeature = 'menus' | 'prep' | 'allergens' | 'stock';

/** Is this beta section open to the signed-in user? ('stock' covers
 *  Receiving, Stocktake and the Order list.) components/ComingSoon.tsx
 *  derives its FeatureKey from this signature. */
export function isFeatureUnlocked(s: Session | undefined, key: BetaFeature): boolean {
  return !!s?.unlocked_features?.includes(key);
}

export async function refreshSession(qc: QueryClient) {
  await qc.invalidateQueries({ queryKey: sessionKey });
  return qc.fetchQuery(sessionQuery);
}
